import styled, { css, keyframes } from 'styled-components';

import { TransientProps } from '@common/types';

type ContainerProps = TransientProps<{ disabled: boolean | undefined }>;
type OpenProps = TransientProps<{ isOpen: boolean }>;
type EmojiItemProps = TransientProps<{
  index: number;
  total: number;
  isOpen: boolean;
}>;
type EmojiCountProps = TransientProps<{ hot: boolean }>;

const floatUpReaction = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  50% {
    opacity: 0.8;
    transform: translateY(-60px) scale(1.15);
  }
  100% {
    opacity: 0;
    transform: translateY(-120px) scale(0.8);
  }
`;

export const Container = styled.div<ContainerProps>`
  ${({ $disabled }) => css`
    position: absolute;
    bottom: 1rem;
    right: 1rem;
    z-index: 30;
    display: flex;
    flex-direction: column;
    align-items: center;

    ${$disabled &&
    css`
      opacity: 0.2;
      filter: grayscale(1);
      pointer-events: none;
    `}
  `};
`;

export const TriggerButton = styled.button<OpenProps>`
  ${({ theme, $isOpen }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 55px;
    height: 55px;
    border-radius: ${theme.radii.full};
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: ${$isOpen
      ? theme.colors.brand.primary.default
      : 'rgba(255, 255, 255, 0.12)'};
    backdrop-filter: blur(10px);
    cursor: pointer;
    transition: all 0.25s ease;
    flex-shrink: 0;

    span {
      font-size: 1.625rem;
      line-height: 1;
    }

    &:hover {
      background: ${$isOpen
        ? theme.colors.brand.primary.default
        : 'rgba(255, 255, 255, 0.22)'};
      transform: scale(1.08);
    }

    &:active {
      transform: scale(0.95);
    }
  `};
`;

export const PlusBadge = styled.span<OpenProps>`
  ${({ $isOpen }) => css`
    position: absolute;
    top: -4px;
    right: -4px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem !important;
    font-weight: 700;
    color: white;
    background: ${$isOpen ? '#e74c3c' : '#45b5aa'};
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform: rotate(${$isOpen ? '45deg' : '0deg'});
  `};
`;

export const ExpandPanel = styled.div<OpenProps>`
  ${({ $isOpen }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    pointer-events: ${$isOpen ? 'auto' : 'none'};
  `};
`;

export const EmojiItem = styled.div<EmojiItemProps>`
  ${({ $index, $total, $isOpen }) => css`
    opacity: ${$isOpen ? 1 : 0};
    transform: ${$isOpen
      ? 'translateY(0) scale(1)'
      : 'translateY(20px) scale(0.8)'};
    transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
    transition-delay: ${$isOpen
      ? `${($total - 1 - $index) * 0.04}s`
      : `${$index * 0.02}s`};
  `};
`;

export const EmojiCircle = styled.button`
  ${() => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(15, 15, 30, 0.85);
    backdrop-filter: blur(14px);
    cursor: pointer;
    transition: all 0.2s ease;

    img {
      width: 34px;
      height: 34px;
      transition: transform 0.2s ease;
    }

    &:hover {
      transform: scale(1.22);
      border-color: #45b5aa;
      box-shadow: 0 0 14px rgba(69, 181, 170, 0.35);

      img {
        transform: scale(1.1);
      }
    }

    &:active {
      transform: scale(0.9);
    }
  `};
`;

export const EmojiCount = styled.span<EmojiCountProps>`
  ${({ $hot }) => css`
    position: absolute;
    top: -2px;
    left: -6px;
    min-width: 20px;
    height: 20px;
    padding: 0 5px;
    border-radius: 10px;
    background: rgba(15, 15, 30, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: ${$hot ? '#f5c842' : 'rgba(255, 255, 255, 0.85)'};
    font-size: 0.65rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    transition: color 0.3s ease;
  `};
`;

export const FloatingReactionsArea = styled.div`
  position: absolute;
  bottom: 78px;
  left: 50%;
  transform: translateX(-50%);
  width: 104px;
  height: 182px;
  pointer-events: none;
  overflow: visible;
`;

export const FloatingEmoji = styled.img`
  position: absolute;
  bottom: 0;
  width: 28px;
  height: 28px;
  pointer-events: none;
  animation: ${floatUpReaction} 2.5s ease-out forwards;
`;
